import type { DashboardUserProfile } from './normalizers'
import { getCurrentFirebaseUser, getFirebaseFirestore } from './client'
import { getHanaspeakApiBaseUrl } from './functions-api'
import { buildLessonProgressMapKey } from './progress-map-keys'
import { traceLearningPerformance } from '@shared/learning/learning-performance-trace'

export function buildLessonProgressMapPatch({
  bookId,
  lessonId,
}: {
  readonly bookId?: string
  readonly lessonId: string
}) {
  return {
    progress_lessons_by_id: {
      [buildLessonProgressMapKey({ lessonId })]: {
        ...(bookId ? { bookId } : {}),
        lessonId,
        progress: 100,
      },
    },
  }
}

export interface LearningLesson {
  readonly audio?: string
  readonly avatar?: string
  readonly bookId?: string
  readonly chapters?: LearningWorkbookChapter[]
  readonly featuredImage?: string
  readonly id: string
  readonly icon?: string
  readonly image?: string
  readonly listening_workbooks?: LearningWorkbookQuizItem[]
  readonly sort?: number
  readonly status?: string
  readonly thumbnail?: string
  readonly title?: string
  readonly translate?: Record<string, string>
}

export interface LearningTranslationEntry {
  readonly code?: string
  readonly text?: string
}

export interface LearningLocalizedTextMap {
  readonly [languageCode: string]: string | undefined
}

export type LearningDisplayTextValue = string | LearningLocalizedTextMap

export interface LearningQuestionItem {
  readonly status?: boolean
  readonly text?: string
  readonly translate?: LearningLocalizedTextMap
}

export interface LearningAnswerOption {
  readonly audio?: string
  readonly answer?: string
  readonly content?: LearningDisplayTextValue
  readonly correct?: boolean
  readonly id?: string
  readonly image?: string
  readonly isRight?: boolean
  readonly label?: LearningDisplayTextValue
  readonly result?: boolean
  readonly status?: boolean
  readonly text?: string
  readonly title?: LearningDisplayTextValue
  readonly translate?: LearningLocalizedTextMap
  readonly value?: string
}

export interface LearningVocabularyItem {
  readonly audio?: string
  readonly featuredImage?: string
  readonly id?: string
  readonly image?: string
  readonly text?: string
  readonly title?: LearningDisplayTextValue
  readonly translate?: LearningLocalizedTextMap
  readonly translates?: LearningTranslationEntry[]
}

export interface LearningConversationItem {
  readonly answer?: string
  readonly audio?: string
  readonly avatar?: string
  readonly fills?: number[]
  readonly id?: string
  readonly image?: string
  readonly isRight?: boolean
  readonly name?: string
  readonly speaker?: string
  readonly status?: boolean
  readonly text?: string
  readonly translate?: LearningLocalizedTextMap
  readonly translates?: LearningTranslationEntry[]
}

export interface LearningContentItem {
  readonly answers?:
    | Array<LearningAnswerOption | string>
    | LearningAnswerOption
    | string
  readonly audio?: string
  readonly avatar?: string
  readonly conversation?: LearningConversationItem[]
  readonly data?: {
    readonly text?: string
  }
  readonly enabledFill?: boolean
  readonly enabledSpeak?: boolean
  readonly examples?: Array<{
    readonly audio?: string
    readonly id?: string
    readonly image?: string
    readonly text?: string
    readonly translate?: LearningLocalizedTextMap
    readonly translates?: LearningTranslationEntry[]
  }>
  readonly featuredImage?: string
  readonly fullwidth?: boolean
  readonly id: string
  readonly image?: string
  readonly isRight?: boolean
  readonly maleAudio?: string
  readonly chapter?: string
  readonly note?: LearningLocalizedTextMap
  readonly oneThird?: boolean
  readonly question?: LearningDisplayTextValue
  readonly questions?: LearningQuestionItem[]
  readonly sort?: number
  readonly status?: boolean
  readonly template?: string
  readonly text?: string
  readonly title?: LearningDisplayTextValue
  readonly translate?: LearningLocalizedTextMap
  readonly translates?: LearningTranslationEntry[]
  readonly type?: string
  readonly vocabularies?: LearningVocabularyItem[]
  readonly youtubeId?: string
}

export interface LearningWorkbookChapter {
  readonly avatar?: string
  readonly bookId?: string
  readonly featuredImage?: string
  readonly id: string
  readonly icon?: string
  readonly image?: string
  readonly implementation?: LearningLocalizedTextMap
  readonly introduction?: LearningLocalizedTextMap
  readonly lessonId?: string
  readonly sort?: number
  readonly status?: string
  readonly thumbnail?: string
  readonly title?: LearningDisplayTextValue
  readonly translate?: LearningLocalizedTextMap
}

export interface LearningWorkbookQuizItem {
  readonly answers?: LearningAnswerOption[]
  readonly audio?: string
  readonly bookId?: string
  readonly data?: {
    readonly text?: string
  }
  readonly featuredImage?: string
  readonly fullwidth?: boolean
  readonly id: string
  readonly image?: string
  readonly lessonId?: string
  readonly oneThird?: boolean
  readonly question?: LearningDisplayTextValue
  readonly questions?: LearningQuestionItem[]
  readonly sort?: number
  readonly status?: string
  readonly template?: string
  readonly text?: string
  readonly title?: LearningDisplayTextValue
  readonly translate?: LearningLocalizedTextMap
  readonly translates?: LearningTranslationEntry[]
  readonly youtubeId?: string
}

export interface LearningLessonDetail {
  readonly avatar?: string
  readonly answers?: Array<LearningAnswerOption | string>
  readonly audio?: string
  readonly content?: LearningContentItem[]
  readonly featuredImage?: string
  readonly id: string
  readonly image?: string
  readonly maleAudio?: string
  readonly questions?: LearningQuestionItem[]
  readonly sort?: number
  readonly status?: string
  readonly template?: string
  readonly text?: string
  readonly thumbnail?: string
  readonly title?: LearningDisplayTextValue
  readonly translates?: LearningTranslationEntry[]
}

function requireLearningFirestore() {
  const firestore = getFirebaseFirestore()

  if (!firestore) {
    throw new Error('Cloud Firestore is not configured for this environment.')
  }

  return firestore
}

function requireCurrentUser() {
  const user = getCurrentFirebaseUser()

  if (!user) {
    throw new Error('Not authorized')
  }

  return user
}

function normalizeSortValue(sort: unknown) {
  if (typeof sort === 'number' && Number.isFinite(sort)) {
    return sort
  }

  if (typeof sort === 'string') {
    const parsedSort = Number(sort)

    if (Number.isFinite(parsedSort)) {
      return parsedSort
    }
  }

  return undefined
}

export async function getPublishedLessons(
  bookId: string,
): Promise<LearningLesson[]> {
  const firestore = requireLearningFirestore()
  const snapshot = await traceLearningPerformance({
    event: 'learning.publishedLessons.query',
    getDetails: (querySnapshot) => ({ bookId, docs: querySnapshot.size }),
    task: () =>
      firestore
        .collection('lessons')
        .where('status', '==', 'published')
        .where('bookId', '==', bookId)
        .orderBy('sort', 'asc')
        .get(),
  })

  const lessons = snapshot.docs.map((document) => ({
    ...(document.data() as Omit<LearningLesson, 'id'>),
    id: document.id,
    sort: normalizeSortValue(document.data().sort),
  }))

  return lessons.sort((left, right) => {
    const leftSort = typeof left.sort === 'number' ? left.sort : 0
    const rightSort = typeof right.sort === 'number' ? right.sort : 0

    return leftSort - rightSort
  })
}

export async function getOnlineBookDetails(
  lessonId: string,
): Promise<LearningLessonDetail[]> {
  const firestore = requireLearningFirestore()
  const snapshot = await traceLearningPerformance({
    event: 'learning.onlineBookDetails.query',
    getDetails: (querySnapshot) => ({ docs: querySnapshot.size, lessonId }),
    task: () =>
      firestore
        .collection('lesson_details')
        .where('status', '==', 'published')
        .where('lessonId', '==', lessonId)
        .orderBy('sort', 'asc')
        .get(),
  })

  const details = snapshot.docs.map((document) => ({
    ...(document.data() as Omit<LearningLessonDetail, 'id'>),
    id: document.id,
    sort: normalizeSortValue(document.data().sort),
  }))

  return details.sort((left, right) => {
    const leftSort = typeof left.sort === 'number' ? left.sort : 0
    const rightSort = typeof right.sort === 'number' ? right.sort : 0

    return leftSort - rightSort
  })
}

export async function getOnlineBookAvailability(
  bookId: string,
): Promise<boolean> {
  const firestore = requireLearningFirestore()
  const snapshot = await traceLearningPerformance({
    event: 'learning.onlineBookAvailability.query',
    getDetails: (querySnapshot) => ({ bookId, docs: querySnapshot.size }),
    task: () =>
      firestore
        .collection('lesson_details')
        .where('bookId', '==', bookId)
        .where('status', '==', 'published')
        .limit(1)
        .get(),
  })

  return !snapshot.empty
}

export async function markLessonAsRead({
  bookId,
  lessonId,
}: {
  readonly bookId?: string
  readonly lessonId: string
}) {
  const user = requireCurrentUser()
  const idToken = await user.getIdToken()
  const response = await fetch(`${getHanaspeakApiBaseUrl()}/learning/mobile/progress/lesson`, {
    body: JSON.stringify({ bookId, lessonId, progress: 100 }),
    headers: {
      Authorization: `Bearer ${idToken}`,
      'Content-Type': 'application/json',
    },
    method: 'POST',
  })
  const payload = await response.json().catch(() => null) as {
    readonly progressLessons?: DashboardUserProfile['progress_lessons']
    readonly error?: unknown
  } | null
  if (!response.ok || !Array.isArray(payload?.progressLessons)) {
    throw new Error(
      typeof payload?.error === 'string'
        ? payload.error
        : 'Unable to save lesson progress right now.',
    )
  }
  return payload.progressLessons
}
